import React, { useState, useEffect } from 'react';
import { Form, Input, Alert } from 'antd';
import { AxiosError } from 'axios';
import { requestPasswordReset } from '../../libs/api';
import Button from '../common/Button';
import styles from '@/assets/styles/ForgotPassword.module.less';
import ThreeBackground from './ThreeBackground';

interface ForgotPasswordForm {
  email: string;
}

interface RateLimitError {
  statusCode: 429;
  message: string;
  error: 'Too Many Requests';
}

// 错误消息映射
const ERROR_MESSAGES: Record<number | 'default', string> = {
  200: '如果该邮箱已注册，重置链接已发送',
  429: '请求过于频繁，请5分钟后再试',
  500: '服务暂时不可用，请稍后重试',
  default: '请求失败，请稍后重试',
};

function getErrorMessage(status?: number): string {
  if (!status) return ERROR_MESSAGES.default;
  return ERROR_MESSAGES[status] || ERROR_MESSAGES.default;
}

// 成功提示消息
const SUCCESS_MESSAGE = `重置链接已发送至您的邮箱。

⏰ 链接有效期：1小时
📧 请检查您的收件箱和垃圾邮件文件夹

如果长时间未收到邮件，请稍后重试。`;

const ForgotPassword: React.FC = () => {
  const [form] = Form.useForm<ForgotPasswordForm>();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0); // 倒计时秒数

  // 监听邮箱字段的值
  const email = Form.useWatch('email', form);

  // 倒计时逻辑
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // 计算按钮是否应该禁用
  const isButtonDisabled =
    loading || // API 处理中
    countdown > 0 || // 倒计时中
    !email || // 未输入邮箱
    form.getFieldsError(['email']).some(({ errors }) => errors.length > 0); // 邮箱格式错误

  const handleSubmit = async (values: ForgotPasswordForm) => {
    setError('');
    setMessage('');
    setLoading(true);

    try {
      await requestPasswordReset({ email: values.email });
      // 无论成功与否都显示相同消息，不暴露用户是否存在
      setMessage(SUCCESS_MESSAGE);
      form.resetFields();
      // 设置60秒倒计时，防止重复提交
      setCountdown(60);
    } catch (err) {
      const axiosError = err as AxiosError<RateLimitError>;
      const status = axiosError.response?.status;

      // 根据状态码显示不同的错误消息
      if (status === 429) {
        setError(ERROR_MESSAGES[429]);
        // 频率限制时设置300秒（5分钟）倒计时
        setCountdown(300);
      } else {
        setError(getErrorMessage(status));
      }

      console.error('Request password reset error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles['auth-page']}>
      <ThreeBackground />
      <div className={styles['auth-container']}>
        <Form
          form={form}
          onFinish={handleSubmit}
          className={styles['auth-form']}
          layout="vertical"
          autoComplete="off"
        >
          <h2>请验证邮箱</h2>
          <p>我们将向您的邮箱发送重置密码链接。</p>

          <Form.Item
            style={{ width: '100%' }}
            name="email"
            rules={[
              { required: true, message: '请输入电子邮件地址' },
              { type: 'email', message: '请输入有效的电子邮件地址' },
              { max: 100, message: '电子邮件地址不能超过100个字符' },
            ]}
          >
            <Input
              className="common-input"
              type="email"
              placeholder="电子邮件"
              size="large"
              disabled={loading}
            />
          </Form.Item>
          {message && (
            <Alert
              message="邮件已发送"
              description={message}
              type="success"
              showIcon
              closable
              onClose={() => {
                setMessage('');
                setCountdown(0);
              }}
              style={{ marginTop: '16px', whiteSpace: 'pre-line' }}
            />
          )}
          {error && (
            <Alert
              message="操作失败"
              description={error}
              type="error"
              showIcon
              closable
              onClose={() => {
                setError('');
                if (countdown > 0) setCountdown(0);
              }}
              style={{ marginTop: '16px' }}
            />
          )}
          <div className={styles['login-link']}>
            <Button type="submit" disabled={isButtonDisabled} className="primaryButton">
              {loading
                ? '发送中...'
                : countdown > 0
                  ? `请等待 ${countdown} 秒后重试`
                  : '发送重置链接'}
            </Button>
            <a href="/login">返回登录</a>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default ForgotPassword;
